import { useNavigate } from "react-router-dom";
import { Button, Modal, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetAppLinksQuery } from "@/redux/apis/config/linksAndDocuments";
import { useAppSelector } from "@/redux/hooks";
import { registrationSelector } from "@/redux/slices/marketing/selectors";
import { useQuickSetupNavigation } from "../hooks";

interface AddDriverModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddDriverModal = ({ open, onClose }: AddDriverModalProps) => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const { providerCode } = useOperator();

  useGetAppLinksQuery();
  const { appLinks } = useAppSelector(registrationSelector);
  const appDownload = appLinks.find((link) => link.document.slug === "app-driver");

  const handleDownloadMobileApp = () => {
    if (appDownload?.document.link) {
      window.open(appDownload.document.link, "_blank");
    }
  };

  const handleDriversPageClick = () => {
    setQuickSetupFlag();
    navigate("/drivers/my-drivers");
    onClose();
  };

  const renderMobileAppSection = () => (
    <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-2.5">
      <div className="flex flex-col gap-2">
        <Typography variant="title" className="text-neutral-dark-gray">
          Add Driver via Mobile App
        </Typography>
        <ol className="ml-6 list-decimal space-y-1">
          <li className="text-base text-neutral-dark-gray">
            <span>Download the Driver App </span>
            {appDownload?.document.link ? (
              <button onClick={handleDownloadMobileApp} className="font-semibold text-info underline hover:text-info-dark">
                here
              </button>
            ) : (
              <span className="text-neutral-silver-gray">(link not available)</span>
            )}
          </li>
          <li className="text-base text-neutral-dark-gray">
            <span>Sign up as a new driver using the</span>
            <br />
            <span>Transport Provider Code: </span>
            <span className="font-semibold uppercase">{providerCode}</span>
          </li>
          <li className="text-base text-neutral-dark-gray">Complete the driver details in the app</li>
          <li className="text-base text-neutral-dark-gray">
            <span>Go to the </span>
            <button onClick={handleDriversPageClick} className="font-semibold text-info underline hover:text-info-dark">
              Drivers
            </button>
            <span> page in the web app to review and approve the driver</span>
          </li>
        </ol>
        <Button variant="primary" size="sm" onClick={handleDownloadMobileApp} disabled={!appDownload?.document.link} className="mt-2 w-fit">
          Download Mobile App
        </Button>
      </div>
    </div>
  );

  const renderWebAppSection = () => (
    <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-2.5">
      <div className="flex flex-col gap-2">
        <Typography variant="title" className="text-neutral-dark-gray">
          Add Driver via Web App
        </Typography>
        <ol className="ml-6 list-decimal space-y-1">
          <li className="text-base text-neutral-dark-gray">
            <span>Go to the </span>
            <button onClick={handleDriversPageClick} className="font-semibold text-info underline hover:text-info-dark">
              Drivers
            </button>
            <span> page</span>
          </li>
          <li className="text-base text-neutral-dark-gray">
            <span>Click </span>
            <span className="font-semibold">Add Driver</span>
            <span> in the action button at the top right</span>
          </li>
          <li className="text-base text-neutral-dark-gray">Enter the driver&apos;s details in the form</li>
          <li className="text-base text-neutral-dark-gray">
            The driver will receive an email with login credentials and a link to download the Driver App
          </li>
          <li className="text-base text-neutral-dark-gray">Driver completes registration in the app</li>
          <li className="text-base text-neutral-dark-gray">
            <span>Go to the </span>
            <button onClick={handleDriversPageClick} className="font-semibold text-info underline hover:text-info-dark">
              Drivers
            </button>
            <span> page in the web app to review and approve the driver</span>
          </li>
        </ol>
        <Button variant="primary" size="sm" onClick={handleDriversPageClick} className="mt-2 w-fit">
          Go to Driver&apos;s Page
        </Button>
      </div>
    </div>
  );

  const renderButtons = () => (
    <Button variant="secondary" size="md" onClick={onClose}>
      Close
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="w-[550px]"
      title="Add Driver"
      description="You can add a driver in two ways:"
      renderButtons={renderButtons}
    >
      <div className="flex flex-col gap-4">
        {renderMobileAppSection()}
        {renderWebAppSection()}
      </div>
    </Modal>
  );
};
